/**
 * Slice 18 — Drop keys (end-of-job dropoff photo evidence).
 * In-app camera only — no gallery; capture metadata stored for claims.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Check, KeyRound, Loader2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { isNetworkFailure } from "@/lib/stampOutbox";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { readSimpleFlowState } from "@/lib/simpleTurnover";
import {
  appendPhotoCaptureRecord,
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import { simpleFlowSegmentIndex } from "@/lib/turnoverWizardPlan";
import { SimpleStepHeader } from "@/components/cleaner/SimpleStepHeader";
import { CLEANER_SIMPLE_CTA_FOOTER, CLEANER_SIMPLE_STEP_SHELL } from "@/lib/turnoverChrome";
import { PhotoNeedNag } from "@/components/cleaner/PhotoNeedNag";

interface Props {
  propertyId: string;
  taskId: string;
  entryWalkthroughState: unknown;
  /** Quiet one-line host hint e.g. "Lockbox · lobby" */
  dropSpotHint?: string | null;
  onComplete: () => void;
  onBack?: () => void;
  onSelectSegment?: (index: number) => void;
  includeMaintenance?: boolean;
  includeQuickCheck?: boolean;
  includeGuestSetup?: boolean;
}

export function SimpleDropKeysStep({
  propertyId,
  taskId,
  entryWalkthroughState,
  dropSpotHint,
  onComplete,
  onBack,
  onSelectSegment,
  includeMaintenance = false,
  includeQuickCheck = false,
  includeGuestSetup = false,
}: Props) {
  const { t } = useTranslation();
  const simple = readSimpleFlowState(entryWalkthroughState);
  const [photoUrl, setPhotoUrl] = useState<string | null>(
    () => simple.keys_drop_photo_url || null,
  );
  const [mode, setMode] = useState<"idle" | "camera">("idle");
  const [busy, setBusy] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const shutterLock = useRef(false);

  const ready = !!photoUrl;
  const hint = dropSpotHint?.trim() || null;
  /** The arrival keys photo is the reference for where the keys go back. */
  const arrivalKeysUrl = simple.keys_photo_url || null;

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError(t("couldNotOpenCamera"));
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.muted = true;
        await video.play().catch(() => undefined);
      }
      setCameraReady(true);
    } catch (err) {
      const name = err instanceof Error ? err.name : "";
      setCameraError(name === "NotAllowedError" ? t("allowCameraAccess") : t("couldNotOpenCamera"));
      setCameraReady(false);
    }
  }, [stopCamera]);

  useEffect(() => {
    if (mode !== "camera") {
      stopCamera();
      return;
    }
    void startCamera();
    return () => stopCamera();
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  const persistPhoto = async (url: string, captureRecord?: Parameters<typeof appendPhotoCaptureRecord>[1]) => {
    const prev = readSimpleFlowState(entryWalkthroughState);
    const { error } = await supabase
      .from("cleaning_tasks")
      .update({
        closeout_keys_photo_url: url,
        closeout_keys_confirmed: true,
        entry_walkthrough_state: {
          ...prev,
          simple_flow: true,
          keys_drop_photo_url: url,
          ...(captureRecord
            ? { photo_captures: appendPhotoCaptureRecord(prev.photo_captures, captureRecord) }
            : {}),
        },
      })
      .eq("id", taskId);
    if (error) throw error;
  };

  const captureFromVideo = async () => {
    if (shutterLock.current || busy) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({
        title: t("cameraRequired"),
        description: t("cameraRequiredDesc"),
        variant: "destructive",
      });
      void startCamera();
      return;
    }
    shutterLock.current = true;
    setBusy(true);
    try {
      const frame = await captureFrameFromVideo(video);
      const { url, record } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId,
        moment: "drop_keys",
        blob: frame.blob,
        width: frame.width,
        height: frame.height,
        storagePath: `${propertyId}/${taskId}/keys-drop/${crypto.randomUUID()}.jpg`,
      });
      setPhotoUrl(url);
      await persistPhoto(url, record);
      setMode("idle");
      stopCamera();
    } catch (err) {
      toast({
        title: t("captureFailed"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
      shutterLock.current = false;
    }
  };

  const handleDone = async () => {
    if (!photoUrl) {
      toast({ title: t("photoWhereYouLeftThem"), variant: "destructive" });
      return;
    }
    setBusy(true);
    try {
      const prev = readSimpleFlowState(entryWalkthroughState);
      const { error } = await supabase
        .from("cleaning_tasks")
        .update({
          closeout_keys_photo_url: photoUrl,
          closeout_keys_confirmed: true,
          entry_walkthrough_state: {
            ...prev,
            simple_flow: true,
            keys_drop_photo_url: photoUrl,
            keys_drop_complete: true,
          },
        })
        .eq("id", taskId);
      // No signal: the keys photo note can wait; the finish itself is queued and still goes through.
      if (error && !isNetworkFailure(error)) throw error;
      stopCamera();
      onComplete();
    } catch (err) {
      toast({
        title: t("couldNotFinish"),
        description: err instanceof Error ? err.message : t("tryAgain"),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cn(CLEANER_SIMPLE_STEP_SHELL, "relative text-[#0f1720]")}>
      <SimpleStepHeader
        title={t("dropKeysTitle")}
        activeSegment={simpleFlowSegmentIndex("drop_keys", { includeMaintenance, includeQuickCheck, includeGuestSetup })}
        includeMaintenance={includeMaintenance}
        includeQuickCheck={includeQuickCheck}
        includeGuestSetup={includeGuestSetup}
        onBack={onBack}
        onSelectSegment={onSelectSegment}
        right={
          ready ? (
            <div className="flex items-center gap-[7px] rounded-full bg-[#24a89a] px-3.5 py-2 text-[14px] font-bold text-white">
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
              {t("done")}
            </div>
          ) : null
        }
        subtitle={
          <>
            {mode === "camera" ? (
              <div className="text-[17px] font-medium text-[#64748b]">{t("photoWhereYouLeftThem")}</div>
            ) : null}
            {hint && mode !== "camera" ? (
              <div className="flex items-center gap-[9px] rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-3.5 py-[11px]">
                <KeyRound className="h-[18px] w-[18px] shrink-0 text-[#64748b]" strokeWidth={2.4} />
                <div className="text-[15px] font-bold text-[#475569]">{hint}</div>
              </div>
            ) : null}
            {hint && mode === "idle" && ready ? (
              <div className="flex items-center gap-[9px] rounded-[13px] border-[1.5px] border-[#e6edf1] bg-[#f4f7f9] px-3.5 py-[11px]">
                <KeyRound className="h-[18px] w-[18px] shrink-0 text-[#64748b]" strokeWidth={2.4} />
                <div className="text-[15px] font-bold text-[#475569]">{hint}</div>
              </div>
            ) : null}
          </>
        }
      />

      {mode === "camera" ? (
        <div className="flex min-h-0 flex-1 flex-col px-5 pb-6">
          <div className="relative flex min-h-0 flex-1 flex-col justify-end overflow-hidden rounded-[20px] bg-[#1c2732]">
            <video
              ref={videoRef}
              playsInline
              muted
              autoPlay
              className={cn("absolute inset-0 h-full w-full object-cover", !cameraReady && "opacity-0")}
            />
            {arrivalKeysUrl ? (
              <div className="absolute right-3 top-3 z-[2] flex flex-col items-end gap-1">
                <img src={arrivalKeysUrl} alt="" className="h-[78px] w-[104px] rounded-[12px] border-2 border-white object-cover" />
                <div className="rounded-full bg-[rgba(13,20,27,0.7)] px-2 py-0.5 text-[12px] font-bold text-white">
                  {t("gwShouldLookLikeKeys")}
                </div>
              </div>
            ) : null}
            {!cameraReady ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <Camera className="h-10 w-10 text-white/50" strokeWidth={1.8} />
                <p className="text-[15px] font-medium text-white/70">{cameraError || t("startingCamera")}</p>
                <button
                  type="button"
                  onClick={() => void startCamera()}
                  className="rounded-full bg-[#24a89a] px-5 py-3 text-[15px] font-bold text-white"
                >
                  {t("openCamera")}
                </button>
                <p className="text-[12px] font-medium text-white/45">
                  {t("photosInAppOnly")}
                </p>
              </div>
            ) : (
              <div className="pointer-events-none absolute left-1/2 top-[46%] h-[140px] w-[140px] -translate-x-1/2 -translate-y-1/2 rounded-[22px] border-2 border-white/20" />
            )}
            <div className="relative z-[1] flex items-center justify-center px-6 pb-6">
              <button
                type="button"
                disabled={busy}
                onClick={() => void captureFromVideo()}
                aria-label={t("capture")}
                className="flex h-24 w-24 items-center justify-center rounded-full border-[5px] border-white/55 active:scale-95 disabled:opacity-60"
              >
                <span className="flex h-[74px] w-[74px] items-center justify-center rounded-full bg-white">
                  {busy ? <Loader2 className="h-7 w-7 animate-spin text-[#24a89a]" /> : null}
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pb-1">
            {arrivalKeysUrl ? (
              <div className="flex shrink-0 items-center gap-3 rounded-[16px] bg-[#f4f7f9] p-2.5">
                <img src={arrivalKeysUrl} alt="" className="h-[60px] w-[80px] shrink-0 rounded-[11px] object-cover" />
                <div className="text-[15.5px] font-bold leading-snug text-[#475569]">{t("gwFoundKeysHere")}</div>
              </div>
            ) : null}
            <div
              className={cn(
                "relative flex min-h-[220px] flex-1 flex-col items-center justify-center overflow-hidden rounded-[20px]",
                photoUrl
                  ? "border-2 border-[#24a89a] bg-[#eff4f7]"
                  : "border-2 border-dashed border-[#d3dee5] bg-[#f4f7f9]",
              )}
            >
              {photoUrl ? (
                <>
                  <img src={photoUrl} alt={t("keysLeftAlt")} className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute bottom-3 left-3 z-10 rounded-lg bg-white px-2.5 py-1 font-mono text-[11px] text-[#475569]">
                    {hint ? `keys · ${hint.toLowerCase()}` : t("keysLeftAlt")}
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-3.5 flex h-[62px] w-[62px] items-center justify-center rounded-[18px] bg-[#e6edf1]">
                    <Camera className="h-7 w-7 text-[#8494a2]" strokeWidth={2.3} />
                  </div>
                  <div className="font-mono text-xs text-[#8494a2]">{t("keysLeftAlt")}</div>
                </>
              )}
            </div>
          </div>
          <div className="px-5 pb-2 pt-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode("camera")}
              className="flex h-[58px] w-full items-center justify-center gap-2.5 rounded-[16px] border-[1.5px] border-[#24a89a] bg-white text-[17px] font-bold text-[#15776d]"
            >
              {photoUrl ? (
                <>
                  <RotateCcw className="h-5 w-5" strokeWidth={2.3} />
                  {t("retake")}
                </>
              ) : (
                <>
                  <Camera className="h-7 w-7" strokeWidth={2.3} />
                  {t("takePhoto")}
                </>
              )}
            </button>
          </div>
        </>
      )}

      {mode === "idle" ? (
        <div className={cn(CLEANER_SIMPLE_CTA_FOOTER, "flex flex-col gap-3")}>
          <PhotoNeedNag propertyId={propertyId} taskId={taskId} variant="drop_keys" />
          <button
            type="button"
            disabled={busy || !ready}
            onClick={() => void handleDone()}
            className={cn(
              "flex h-[68px] w-full items-center justify-center rounded-[18px] text-[23px] font-bold",
              ready ? "bg-[#24a89a] text-white hover:bg-[#1f958a]" : "bg-[#eff4f7] text-[#a5b3bf]",
            )}
          >
            {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : t("finishJob")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
